import React, { useRef, Component } from 'react';
import classnames from 'classnames';
import Highlight from 'react-highlight-words';
import * as Sentry from '@sentry/react';
import { observer } from 'mobx-react';
import { Card, EntityDetail } from '../../../../common/components/WebComponents';
import { useIsHovered } from '../../../../common/hooks/useIsHovered';
import CallButton from '../../../../common/components/Call/CallButton';
import VideoCallButton from '../../../../common/components/Call/VideoCallButton';
import mobxInjectSelect from '../../../../common/utils/mobxInjectSelect';
import SmsOptedOut from '../../../../common/components/SmsOptedOut';

import { isTokenTigerPage } from '../../../../common/utils';
import {
  Organization,
  Entity,
  User,
  PatientEntity,
  PatientContactEntity,
  TeamEntity,
  Conversation,
  GroupTypes,
  RoleGroupEntity,
  Role as RoleType,
} from '../../../../types';
import styles from './SearchParityResults.module.css';
import Item from './SearchItemResult';
import { PatientReferenceInfo } from 'common/components/PatientContext';
import { groupByEntity, validateEntityOrder } from 'common/utils/searchParity';
import { getNotInConversationLabel } from 'common/utils/getNotInConversationLabel';

type PresenceStatuses = 'available' | 'unavailable' | 'dnd' | 'dndAf' | undefined;

type SearchParityResultsProps = {
  network: 'provider' | 'patient';
  results: Entity[];
  showPresenceIndicator?: boolean;
  presenceSize?: 'large' | 'medium' | 'small' | 'tiny';
  typeOfUser?: 'searchSideBarUser' | 'composeUser';
  query: string;
};

type MobxProps = {
  currentOrganization: Organization;
  currentUserId: string;
  entitySortMap: Map<string, number>;
  hideDnd: boolean;
  isAddFamilyAndContacts: boolean;
  isUserDndAfEnabledForCurrentOrg: (user: User) => boolean;
  isGroupVideoCallEnabled: boolean;
  isGroupVoiceCallEnabled: boolean;
  isPFVideoCallEnabled: boolean;
  isVideoCallEnabled: boolean;
  isVoiceCallEnabled: boolean;
  shouldValidateSearchResults: boolean;
  subscribeUser: (userid: string) => void;
  unsubscribeUser: (userid: string) => void;
  currentConversation: Conversation;
  isPatientContextAllowed: boolean;
  isSearchCustomAttributesFeatureFlagEnabled: boolean;
  input: string;
};

const Highlighter = ({
  text = '',
  searchText = '',
  style,
}: {
  text: string;
  searchText: string;
  style?: string;
}) => {
  return (
    <Highlight
      className={style || styles.providerHighlight}
      textToHighlight={text}
      searchWords={searchText.split(',')}
      autoEscape
    />
  );
};

export const Team = observer(
  ({
    query,
    team,
    varient = 'team',
    dataTestId,
  }: {
    query?: string;
    team: TeamEntity;
    varient?: 'team' | 'activated_team';
    dataTestId?: string;
  }) => {
    const tagName = team.tag?.displayName;
    const tagColor = team.tag?.color;
    if (varient === 'activated_team') {
      return (
        <EntityDetail
          dataTestId={dataTestId}
          avatar={{
            type: 'team',
            ...(tagColor && { bgColor: tagColor }),
          }}
          name={
            query ? <Highlighter text={team.displayName} searchText={query} /> : team.displayName
          }
          options={{
            type: 'search results',
            details: {
              theme: 'provider',
              ...(team.description && { subtext: team.description }),
              ...(tagName && {
                description: query ? <Highlighter text={tagName} searchText={query} /> : tagName,
              }),
            },
          }}
        />
      );
    }
    return (
      <EntityDetail
        dataTestId={dataTestId}
        avatar={{
          type: 'initials',
          initialsString: team.displayName.split(' ').slice(0, 2).join(' '),
          ...(!tagColor && { initialsTheme: 'team' }),
          ...(tagColor && { bgColor: tagColor }),
        }}
        name={query ? <Highlighter text={team.displayName} searchText={query} /> : team.displayName}
        options={{
          type: 'search results',
          details: {
            theme: 'provider',
            ...(team.description && { subtext: team.description }),
            ...(tagName && {
              description: query ? <Highlighter text={tagName} searchText={query} /> : tagName,
            }),
          },
        }}
      />
    );
  }
);

type RoleProps = {
  role: Entity;
  isEntityInConversationCheck?: boolean;
  query?: string;
  subscribeUser?: (userid: string) => void;
  unsubscribeUser?: (userid: string) => void;
  currentConversation?: Conversation;
  currentUserId?: string;
  selectedRecipients?: Entity[];
  rightBottomTag?: {
    displayName: string;
    tagColor: string;
  };
  lastMessage?: string;
  dataTestId?: string;
  isKeywordSearch?: boolean;
};

class RoleClass extends Component<RoleProps> {
  componentDidMount() {
    const { role, subscribeUser } = this.props;
    const roleUser = role?.members && role.members.length === 1 && role.members[0];
    if (roleUser) {
      subscribeUser?.(roleUser.id);
    }
  }

  componentDidUpdate(prevProps: RoleProps) {
    const { role, subscribeUser, unsubscribeUser } = this.props;
    const { role: prevPropRole } = prevProps;
    const roleUser = (role?.members && role.members.length === 1 && role.members[0]) as User;
    const prevRoleUser = (prevPropRole?.members &&
      prevPropRole.members.length === 1 &&
      prevPropRole.members[0]) as User;

    unsubscribeUser?.(prevRoleUser.id);
    subscribeUser?.(roleUser.id);
  }

  componentWillUnmount() {
    const { role, unsubscribeUser } = this.props;
    const roleUser = (role?.members && role.members.length === 1 && role.members[0]) as User;
    unsubscribeUser?.(roleUser.id);
  }

  render() {
    const {
      query,
      role,
      isEntityInConversationCheck,
      currentConversation,
      selectedRecipients,
      rightBottomTag,
      lastMessage,
      dataTestId,
      isKeywordSearch,
      currentUserId,
    } = this.props;
    const roleUser = (role?.members && role.members.length === 1 && role.members[0]) as User;

    let presenceStatus: PresenceStatuses;
    if (roleUser?.presenceStatus === 'AVAILABLE') {
      presenceStatus = 'available';
    } else {
      presenceStatus = undefined;
    }

    const notInConversationLabel = isEntityInConversationCheck
      ? getNotInConversationLabel(
          currentConversation,
          isEntityInConversationCheck,
          role?.botUser?.id || role.id,
          selectedRecipients,
          currentUserId
        )
      : null;

    const buildMessageAndRightTag = (
      <div>
        {lastMessage && (
          <div className={styles.lastMessage}>
            {query ? <Highlighter text={lastMessage} searchText={query} /> : lastMessage}
          </div>
        )}
        {rightBottomTag && (
          <div className={styles.roleStatus}>
            <span className={styles.roleName}>
              {query ? (
                <Highlighter text={rightBottomTag.displayName} searchText={query} />
              ) : (
                rightBottomTag.displayName
              )}
            </span>
            <span
              className={styles.tagIndicator}
              style={{ backgroundColor: rightBottomTag.tagColor }}
            />
          </div>
        )}
      </div>
    );

    return (
      <div className={styles.customizableEntity}>
        <EntityDetail
          dataTestId={dataTestId}
          avatar={{
            type: 'role',
            shape: 'squircle',
            ...(role.tag && !role.tag.isDefault && { bgColor: role.tag.color }),
            presenceStatus,
            presenceSize: 'small',
          }}
          name={
            query ? (
              <Highlighter text={role.displayName} searchText={query} />
            ) : (
              <span className={classnames({ [styles.nameContainer]: isEntityInConversationCheck })}>
                {role.displayName}
              </span>
            )
          }
          options={{
            type: 'search results',
            details: {
              theme: 'provider',
              subtext:
                query && role.tag?.displayName ? (
                  <Highlighter text={role.tag?.displayName} searchText={query} />
                ) : (
                  role.tag?.displayName
                ),
              description:
                (lastMessage && lastMessage !== '') || rightBottomTag
                  ? buildMessageAndRightTag
                  : role.members && role.members.length
                  ? `On Duty: ${role.members[0].displayName}`
                  : isKeywordSearch
                  ? ''
                  : 'No one is on duty',
            },
          }}
        />
        {notInConversationLabel ? (
          <div className={styles.textContainer}>
            <span className={styles.notInGroupText}>{notInConversationLabel}</span>
          </div>
        ) : null}
      </div>
    );
  }
}

export const Role = observer(RoleClass);

export const Patient = ({ patient, query }: { patient: PatientEntity; query?: string }) => {
  const {
    patient: { mrn, dob, gender, smsOptedOut },
  } = patient;

  const patientInformation = [
    mrn ? `MRN ${mrn}` : null,
    dob ? `DOB ${dob}` : null,
    gender ? gender : null,
  ]
    .filter((v) => v)
    .join(' | ');

  return (
    <EntityDetail
      avatar={{ type: 'patient', smsOptedOut }}
      name={
        query ? (
          <Highlighter
            style={styles.patientHighlight}
            text={patient?.displayName}
            searchText={query}
          />
        ) : (
          patient.displayName
        )
      }
      options={{
        type: 'search results',
        details: { theme: 'patient', patientInformation },
        smsOptedOut,
        smsOptedOutSubText: 'Opted out via SMS',
      }}
    />
  );
};

export const PatientContact = observer(
  ({ patientContact }: { patientContact: PatientContactEntity }) => {
    const { smsOptedOut } = patientContact.patientContact;
    return (
      <EntityDetail
        avatar={{ type: 'empty' }}
        name={patientContact.displayName}
        options={{
          type: 'search results',
          details: {
            theme: 'patientContact',
            relation: patientContact.patientContact.relation,
          },
          smsOptedOut,
          smsOptedOutSubText: 'Opted out via SMS',
        }}
      />
    );
  }
);

export const Individual = observer(
  ({
    currentConversation,
    currentOrganization,
    currentUserId,
    hideDnd,
    individual,
    isDisabled,
    showPresenceIndicator,
    presenceSize,
    showDndAfStatus,
    query,
    isEntityInConversationCheck,
    typeOfUser = 'composeUser',
    selectedRecipients,
    message,
    rightBottomTag,
    dataTestId,
    isSearchCustomAttributesFeatureFlagEnabled,
    input,
  }: {
    currentConversation?: Conversation;
    currentOrganization?: Organization;
    currentUserId?: string;
    hideDnd?: boolean;
    individual: Entity;
    isDisabled?: boolean;
    showPresenceIndicator?: boolean | undefined;
    presenceSize?: 'large' | 'medium' | 'small' | 'tiny' | undefined;
    showDndAfStatus?: boolean;
    typeOfUser?: 'searchSideBarUser' | 'composeUser';
    isEntityInConversationCheck?: boolean;
    query?: string;
    selectedRecipients?: Entity[];
    message?: string;
    rightBottomTag?: {
      displayName: string;
      tagColor: string;
    };
    dataTestId?: string;
    isSearchCustomAttributesFeatureFlagEnabled?: boolean;
    input?: string;
  }) => {
    let presenceStatus: PresenceStatuses;
    const subtext =
      message && message !== ''
        ? message
        : [individual.title, individual.department].filter((v) => v).join(', ');

    const hasPresenceIndicator =
      showPresenceIndicator &&
      !individual.isPatient &&
      !individual.isPatientContact &&
      !individual.isVisitor &&
      !(individual.id && isTokenTigerPage(individual.id));

    let initialsString = [individual.firstName, individual.lastName]
      .filter((v) => v)
      .filter((v) => v)
      .join(' ');
    if (!initialsString.length) initialsString = individual.displayName;

    if (individual.dnd && showDndAfStatus) {
      presenceStatus = 'dndAf';
    } else if (individual.dnd && (currentUserId !== individual.id || individual || !hideDnd)) {
      presenceStatus = 'dnd';
    } else if (currentUserId === individual.id || individual.presenceStatus === 'AVAILABLE') {
      presenceStatus = 'available';
    } else {
      presenceStatus = undefined;
    }

    const notInConversationLabel = isEntityInConversationCheck
      ? getNotInConversationLabel(
          currentConversation,
          isEntityInConversationCheck,
          individual.id,
          selectedRecipients,
          currentUserId
        )
      : null;

    const buildRightTag = rightBottomTag && (
      <div className={styles.roleStatus}>
        <span className={styles.roleName}>
          {query ? (
            <Highlighter text={rightBottomTag.displayName} searchText={query} />
          ) : (
            rightBottomTag.displayName
          )}
        </span>
        <span
          className={styles.tagIndicator}
          style={{ backgroundColor: rightBottomTag.tagColor }}
        />
      </div>
    );

    const buildCustomFields = (
      customFields: typeof individual.customFields,
      individualId: string,
      searchQuery?: string
    ) => {
      if (
        !customFields ||
        customFields.length === 0 ||
        !isSearchCustomAttributesFeatureFlagEnabled ||
        (!searchQuery && !input)
      )
        return null;

      const rawQuery = searchQuery ?? input ?? '';
      const lowerSearchQuery = rawQuery.toLowerCase();

      const filteredFields = customFields
        .map((field) => {
          const parts = field.value.split('|').map((part) => part.trim());
          const matchingParts = parts.filter((part) =>
            part.toLowerCase().includes(lowerSearchQuery)
          );

          if (matchingParts.length === 0) return null;

          return {
            name: field.name,
            value: matchingParts.join(', '),
          };
        })
        .filter((field): field is { name: string; value: string } => field !== null);

      if (filteredFields.length === 0) return null;

      return filteredFields.map((field, index) => (
        <div key={index} className={styles.customField}>
          <div
            className={styles.customField}
            data-testid={`search-custom-field-${index}-${individualId}`}
          >
            <Highlighter text={field.name} searchText={rawQuery} />
            {': '}
            <Highlighter text={field.value} searchText={rawQuery} />
          </div>
        </div>
      ));
    };

    return (
      <div className={styles.customizableEntity}>
        <EntityDetail
          dataTestId={dataTestId}
          avatar={{
            ...(individual.avatarUrl
              ? {
                  type: 'image',
                  imgSrc: individual.avatarUrl,
                  presenceStatus: hasPresenceIndicator ? presenceStatus : undefined,
                  presenceSize,
                }
              : {
                  initialsTheme: 'user',
                  isDisabled,
                  theme: currentOrganization?.isContacts ? 'empty' : undefined,
                  type: currentOrganization?.isContacts ? typeOfUser : 'initials',
                  initialsString,
                  presenceStatus: hasPresenceIndicator ? presenceStatus : undefined,
                  presenceSize,
                }),
          }}
          name={
            query ? (
              <Highlighter text={individual.displayName} searchText={query} />
            ) : (
              <span
                className={classnames({
                  [styles.isDisabled]: isDisabled,
                  [styles.nameContainer]: isEntityInConversationCheck,
                })}
              >
                {individual.displayName}
              </span>
            )
          }
          options={{
            type: 'search results',
            details: {
              theme: 'provider',
              subtext: query ? (
                <>
                  <Highlighter text={subtext} searchText={query} />
                  {buildRightTag}
                </>
              ) : (
                <>
                  {subtext}
                  {buildRightTag}
                </>
              ),
              customFields: buildCustomFields(individual.customFields, individual.id, query),
            },
          }}
        />
        {notInConversationLabel ? (
          <div className={styles.textContainer}>
            <span className={styles.notInGroupText}>{notInConversationLabel}</span>
          </div>
        ) : null}
      </div>
    );
  }
);

const PROVIDER_NETWORK_MEMBER_LIMIT = 10;

export const Group = observer(
  ({
    group,
    query,
    dataTestId,
    isPatientContextAllowed,
  }: {
    group: Entity;
    query?: string;
    dataTestId?: string;
    isPatientContextAllowed?: boolean;
  }) => {
    const avatarUrl = group.avatarUrl;
    const patientReferencePill = group.patientContextDetails && isPatientContextAllowed && (
      <div className={styles.patientReferencePill}>
        <PatientReferenceInfo
          patientReferenceId={group.patientContextId}
          patientReference={group.patientContextDetails}
          type={'pill'}
        />
      </div>
    );
    return (
      <EntityDetail
        dataTestId={dataTestId}
        avatar={avatarUrl ? { type: 'image', imgSrc: avatarUrl } : { type: 'group' }}
        name={
          query ? <Highlighter text={group.displayName} searchText={query} /> : group.displayName
        }
        options={{
          type: 'search results',
          details: { theme: 'provider', patientContext: patientReferencePill },
        }}
      />
    );
  }
);

export const DistributionList = observer(
  ({
    distributionList,
    query,
    dataTestId,
  }: {
    distributionList: Entity;
    query?: string;
    dataTestId?: string;
  }) => {
    return (
      <EntityDetail
        dataTestId={dataTestId}
        avatar={{ type: 'broadcast' }}
        name={
          query ? (
            <Highlighter text={distributionList.displayName} searchText={query} />
          ) : (
            distributionList.displayName
          )
        }
        options={{ type: 'search results', details: { theme: 'provider' } }}
      />
    );
  }
);

export const Forum = observer(
  ({ forum, query, dataTestId }: { forum: Entity; query?: string; dataTestId?: string }) => {
    const avatarUrl = forum?.avatarUrl;
    return (
      <EntityDetail
        dataTestId={dataTestId}
        avatar={avatarUrl ? { type: 'image', imgSrc: avatarUrl } : { type: 'forum' }}
        name={
          query ? <Highlighter text={forum.displayName} searchText={query} /> : forum.displayName
        }
        options={{ type: 'search results', details: { theme: 'provider' } }}
      />
    );
  }
);

export const RoleGroup = observer(
  ({
    currentOrganization,
    currentUserId,
    query,
    roleGroup,
    userDndAfStatusForCurrentOrg,
    isKeywordSearch,
    dataTestId,
  }: {
    currentOrganization?: Organization;
    currentUserId?: string;
    query?: string;
    roleGroup: RoleGroupEntity;
    userDndAfStatusForCurrentOrg?: boolean;
    isKeywordSearch?: boolean;
    dataTestId?: string;
  }) => {
    const { p2pSender, p2pRecipient, conversation } = roleGroup;
    const lastMessage = conversation?.lastMessage?.body || '';

    if (p2pRecipient?.$entityType === 'user') {
      const individual = p2pRecipient as unknown as Entity;

      const rightBottomTag = {
        displayName: p2pSender?.displayName || '',
        tagColor: p2pSender?.tag?.color?.replace('0x', '#') || '',
      };

      return (
        <Individual
          individual={individual}
          showPresenceIndicator
          showDndAfStatus={userDndAfStatusForCurrentOrg}
          currentUserId={currentUserId}
          query={query}
          currentOrganization={currentOrganization}
          presenceSize={'small'}
          rightBottomTag={rightBottomTag}
          message={lastMessage}
          dataTestId={dataTestId}
        />
      );
    }

    if (p2pRecipient?.$entityType === 'role') {
      const role = p2pRecipient as unknown as RoleType;
      let rightBottomTag = undefined;
      if (p2pRecipient?.tag) {
        role.tag = p2pRecipient?.tag;
      }

      if (p2pSender?.$entityType === 'role') {
        rightBottomTag = {
          displayName: p2pSender?.displayName || '',
          tagColor: p2pSender?.tag?.color?.replace('0x', '#') || '',
        };
      }

      return (
        <Role
          role={role as unknown as Entity}
          query={query}
          rightBottomTag={rightBottomTag}
          lastMessage={lastMessage}
          dataTestId={dataTestId}
          isKeywordSearch={isKeywordSearch}
        />
      );
    }

    return null;
  }
);

const SearchResults = ({
  currentOrganization,
  currentUserId,
  entitySortMap,
  hideDnd,
  isAddFamilyAndContacts,
  isUserDndAfEnabledForCurrentOrg,
  isGroupVideoCallEnabled,
  isGroupVoiceCallEnabled,
  isPFVideoCallEnabled,
  isVideoCallEnabled,
  isVoiceCallEnabled,
  network,
  presenceSize,
  query,
  results,
  showPresenceIndicator,
  shouldValidateSearchResults,
  subscribeUser,
  unsubscribeUser,
  typeOfUser,
  currentConversation,
  isPatientContextAllowed,
  isSearchCustomAttributesFeatureFlagEnabled,
  input,
}: SearchParityResultsProps & MobxProps) => {
  const groupedEntities = groupByEntity(currentOrganization, entitySortMap, results);

  if (shouldValidateSearchResults) {
    const entityTypes = groupedEntities.map((entity) => entity.entity_type);
    const isValid = validateEntityOrder(entityTypes);
    if (!isValid) {
      const e = new Error(
        `SearchSidebar Invalid entity order; entityTypes: ${entityTypes}; entitySortMap: ${Array.from(
          entitySortMap.entries()
        )}`
      );
      Sentry.captureException(e);
    }
  }

  const IndividualItem = ({
    currentOrganization,
    currentUserId,
    hideDnd,
    individual,
    isLast,
    presenceSize,
    showDndAfStatus,
    showPresenceIndicator,
    typeOfUser,
    itemKey,
    isSearchCustomAttributesFeatureFlagEnabled,
  }: {
    currentOrganization: Organization;
    currentUserId: string;
    hideDnd: boolean;
    individual: Entity;
    isLast: boolean;
    presenceSize?: 'large' | 'medium' | 'small' | 'tiny' | undefined;
    showDndAfStatus?: boolean;
    showPresenceIndicator?: boolean | undefined;
    typeOfUser?: 'searchSideBarUser' | 'composeUser';
    itemKey: string | number;
    isSearchCustomAttributesFeatureFlagEnabled?: boolean;
    input?: string;
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const isHovered = useIsHovered(ref);

    return (
      <div ref={ref}>
        <Item itemKey={itemKey} entity={individual} lastItem={isLast}>
          <div className={styles.entityDetail}>
            <Individual
              query={query}
              currentOrganization={currentOrganization}
              currentUserId={currentUserId}
              hideDnd={hideDnd}
              individual={individual}
              showDndAfStatus={showDndAfStatus}
              showPresenceIndicator={showPresenceIndicator}
              presenceSize={presenceSize}
              typeOfUser={typeOfUser}
              isSearchCustomAttributesFeatureFlagEnabled={
                isSearchCustomAttributesFeatureFlagEnabled
              }
              input={input}
            />
          </div>
          {isHovered && (isVideoCallEnabled || isVoiceCallEnabled) && (
            <div className={styles.callButton}>
              <CallButton entity={individual} origin={'Search'} />
            </div>
          )}
        </Item>
      </div>
    );
  };

  const RoleItem = ({
    index,
    lastItem,
    role,
    currentUserId,
    currentConversation,
  }: {
    index: number;
    lastItem: boolean;
    role: Entity;
    currentUserId: string;
    currentConversation: Conversation;
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const isHovered = useIsHovered(ref);
    let callEntity = undefined;

    if (role.members.length) {
      callEntity = {
        recipientName: role.displayName,
        members: [],
        entityType: 'group',
        groupType: GroupTypes.ROLE_P2P,
        id: role.members[0].id,
        recipientId: role.members[0].id,
        p2pRecipient: role,
      };
    }
    return (
      <div ref={ref}>
        <Item key={index} entity={role} lastItem={lastItem}>
          <Role
            role={role}
            query={query}
            subscribeUser={subscribeUser}
            unsubscribeUser={unsubscribeUser}
            currentUserId={currentUserId}
            currentConversation={currentConversation}
          />
          {isHovered &&
            (isVideoCallEnabled || isVoiceCallEnabled) &&
            role.members?.length > 0 &&
            callEntity && (
              <div className={styles.callButton}>
                <CallButton entity={callEntity} origin={'Search'} />
              </div>
            )}
        </Item>
      </div>
    );
  };

  const GroupItem = ({
    group,
    isLast,
    isPatientContextAllowed,
  }: {
    group: Entity;
    isLast: boolean;
    isPatientContextAllowed?: boolean;
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const isHovered = useIsHovered(ref);

    return (
      <div ref={ref}>
        <Item entity={group} lastItem={isLast}>
          <div className={styles.entityDetail}>
            <Group group={group} query={query} isPatientContextAllowed={isPatientContextAllowed} />
          </div>
          {isHovered &&
            (isGroupVideoCallEnabled || isGroupVoiceCallEnabled) &&
            group.memberCount <= PROVIDER_NETWORK_MEMBER_LIMIT && (
              <div className={styles.callButton}>
                <CallButton entity={group} origin={'Search'} />
              </div>
            )}
        </Item>
      </div>
    );
  };

  return (
    <div className={styles.results}>
      {groupedEntities.map((entities, index) => {
        if (entities.entity_type === 'team') {
          return (
            <Card key={`team-${index}`} header="Teams" theme="provider" type="category">
              {entities.values.map((team, index) => {
                if (team.$entityType === 'team') {
                  const lastItem = index === entities.values.length - 1;
                  return (
                    <Item key={index} entity={team} lastItem={lastItem}>
                      <Team team={team} query={query} />
                    </Item>
                  );
                }
                return <></>;
              })}
            </Card>
          );
        }

        if (entities.entity_type === 'role') {
          return (
            <Card key={`role-${index}`} header="Roles" theme="provider" type="category">
              {entities.values.map((role, index) => {
                const lastItem = index === entities.values.length - 1;
                return (
                  <RoleItem
                    key={index}
                    index={index}
                    lastItem={lastItem}
                    role={role}
                    currentUserId={currentUserId}
                    currentConversation={currentConversation}
                  />
                );
              })}
            </Card>
          );
        }

        if (entities.entity_type === 'user') {
          if (network === 'patient') {
            return (
              <Card
                key={`user-patient-${index}`}
                header="Individuals"
                theme="patient"
                type="category"
              >
                {entities.values.map((patient, index) => {
                  const lastItem = index === entities.values.length - 1;
                  if (patient.isPatient && 'patient' in patient) {
                    return (
                      <Item key={index} entity={patient} lastItem={lastItem}>
                        <div className={styles.entityDetail}>
                          <Patient patient={patient} query={query} />
                          {patient.patient.smsOptedOut && (
                            <div className={styles.smsOptedOut}>
                              <SmsOptedOut tooltip={!isAddFamilyAndContacts} />
                            </div>
                          )}
                        </div>
                        {isPFVideoCallEnabled && !patient.patient.smsOptedOut && (
                          <div className={styles.callButton}>
                            <VideoCallButton
                              entity={{
                                recipientId: patient.id,
                                recipientName: patient.displayName,
                              }}
                              isSmall={true}
                              origin={'Search'}
                            />
                          </div>
                        )}
                      </Item>
                    );
                  }
                  if (patient.isPatientContact && 'patientContact' in patient) {
                    return (
                      <Item key={index} entity={patient} lastItem={lastItem}>
                        <div className={styles.entityDetail}>
                          <PatientContact patientContact={patient} />
                          {patient.patientContact.smsOptedOut && (
                            <div className={styles.smsOptedOut}>
                              <SmsOptedOut tooltip={!isAddFamilyAndContacts} />
                            </div>
                          )}
                        </div>
                        {isPFVideoCallEnabled && !patient.patientContact.smsOptedOut && (
                          <div className={styles.callButton}>
                            <VideoCallButton
                              entity={{
                                recipientId: patient.id,
                                recipientName: patient.displayName,
                              }}
                              isSmall={true}
                              origin={'Search'}
                            />
                          </div>
                        )}
                      </Item>
                    );
                  }
                  return <></>;
                })}
              </Card>
            );
          }
          return (
            <Card
              key={`user-provider-${index}`}
              header="Individuals"
              theme="provider"
              type="category"
            >
              {entities.values.map((individual, index) => {
                const lastItem = index === entities.values.length - 1;
                const userDndAfStatusForCurrentOrg = isUserDndAfEnabledForCurrentOrg(
                  individual as unknown as User
                );
                return (
                  <IndividualItem
                    key={index}
                    itemKey={index}
                    currentOrganization={currentOrganization}
                    currentUserId={currentUserId}
                    hideDnd={hideDnd}
                    individual={individual}
                    isLast={lastItem}
                    presenceSize={presenceSize}
                    showDndAfStatus={userDndAfStatusForCurrentOrg}
                    showPresenceIndicator={showPresenceIndicator}
                    typeOfUser={typeOfUser}
                    isSearchCustomAttributesFeatureFlagEnabled={
                      isSearchCustomAttributesFeatureFlagEnabled
                    }
                    input={input}
                  ></IndividualItem>
                );
              })}
            </Card>
          );
        }

        if (entities.entity_type === 'group') {
          return (
            <Card key={`group-${index}`} header="Groups" theme="provider" type="category">
              {entities.values.map((group, index) => {
                const lastItem = index === entities.values.length - 1;
                if (group.groupType === 'ACTIVATED_TEAM') {
                  return (
                    <Item key={index} entity={group} lastItem={lastItem}>
                      <Team team={group as TeamEntity} varient="activated_team" query={query} />
                    </Item>
                  );
                }
                return (
                  <GroupItem
                    group={group}
                    isLast={lastItem}
                    isPatientContextAllowed={isPatientContextAllowed}
                  />
                );
              })}
            </Card>
          );
        }

        if (entities.entity_type === 'distributionList') {
          return (
            <Card
              key={`distributionList-${index}`}
              header="Broadcast"
              theme={network}
              type="category"
            >
              {entities.values.map((distributionList, index) => {
                const lastItem = index === entities.values.length - 1;
                return (
                  <Item key={index} entity={distributionList} lastItem={lastItem}>
                    <DistributionList distributionList={distributionList} query={query} />
                  </Item>
                );
              })}
            </Card>
          );
        }

        if (entities.entity_type === 'forum') {
          return (
            <Card key={`forum-${index}`} header="Forums" theme="provider" type="category">
              {entities.values.map((forum, index) => {
                const lastItem = index === entities.values.length - 1;
                return (
                  <Item key={index} entity={forum} lastItem={lastItem}>
                    <Forum forum={forum} query={query} />
                  </Item>
                );
              })}
            </Card>
          );
        }
        return <></>;
      })}
    </div>
  );
};

export default mobxInjectSelect<SearchParityResultsProps, MobxProps>({
  callStore: [
    'isGroupVideoCallEnabled',
    'isGroupVoiceCallEnabled',
    'isPFVideoCallEnabled',
    'isVideoCallEnabled',
    'isVoiceCallEnabled',
  ],
  composeMessageStore: ['isAddFamilyAndContacts'],
  messengerStore: [
    'currentOrganization',
    'shouldValidateSearchResults',
    'isPatientContextAllowed',
    'isSearchCustomAttributesFeatureFlagEnabled',
  ],
  presenceStore: ['subscribeUser', 'unsubscribeUser'],
  searchSidebarStore: ['entitySortMap'],
  sessionStore: ['currentUserId'],
  userStore: ['hideDnd', 'isUserDndAfEnabledForCurrentOrg'],
  recipientPickerStore: ['input'],
})(SearchResults);
